'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var Button = require('../../Button/src/Button.js');
var index = require('../../Menu/index.js');
var Popover = require('../../Popover/src/Popover.js');
require('../../Typography/src/Heading.js');
require('../../Typography/src/Link.js');
require('../../Typography/src/ListItem.js');
require('../../Typography/src/OrderedList.js');
var Text = require('../../Typography/src/Text.js');
require('../../Typography/src/UnorderedList.js');
var Dropdown_module = require('../styles/Dropdown.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-statements */
const defaultEmptyContentText = 'К сожалению, не получилось загрузить список, скоро всё поправим';
const minWidthModifier = {
    name: 'minWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        state.styles.popper.minWidth = '280px';
    },
};
/** Helper function, return object with positioner settings */
const makePositionerSettings = (placement, matchWidth, flip) => ({
    placement,
    modifiers: matchWidth ? [] : [minWidthModifier],
    matchWidth,
    flip,
});
/**
 * @description Dropdown component to select value from options
 *
 * @component
 * @example
 * ```jsx
 * <Dropdown options={options}>{triggerElement}</Dropdown>
 * ```
 */
const Dropdown = React__namespace.forwardRef((props, ref) => {
    const { defaultValue, value, placement = 'bottom-start', mode, mobile, fullscreen = true, matchWidth = false, options, disabled, readonly, header, emptyContent, optionsSort = true, className, children, closeOnSelect = true, flip = true, onClick, onChange, onOpen, onClose, ...restProps } = props;
    const isFirstRender = React__namespace.useRef(true);
    const [visible, setVisible] = reactUtils.useBoolean(false);
    const focused = React__namespace.useRef(false);
    const [innerValue, setInnerValue] = reactUtils.useStateCallback(reactUtils.isDefined(value) ? value : defaultValue);
    const [innerOptions, setInnerOptions] = React__namespace.useState(options || []);
    const [focusedIndex, setFocusedIndex] = React__namespace.useState(-1);
    const isMultiple = mode === 'multiple';
    const cx = reactUtils.useStyles(Dropdown_module.default);
    const popoverClassNames = cx(className, 'container', {
        dropdown: !mobile,
    });
    let triggerElement = null;
    // Ensure Dropdown has only one child node
    const count = React__namespace.useMemo(() => React__namespace.Children.count(children), [children]);
    const condition = count === 0 || count > 1;
    if (condition) {
        reactUtils.error({
            condition,
            message: 'Ensure Dropdown has only one child node',
        })();
        triggerElement = children;
    }
    else {
        const child = React__namespace.Children.only(children);
        const handleClickTrigger = (event) => {
            if (disabled)
                return;
            !readonly && setVisible.toggle();
            focused.current = true;
            child.props.onClick?.(event);
        };
        const handleFocusBlurTrigger = (event) => {
            if (event.type === 'focus') {
                focused.current = true;
                child.props.onFocus?.(event);
            }
            else {
                focused.current = false;
                child.props.onBlur?.(event);
            }
        };
        const triggerProps = {
            ...child.props,
            tabIndex: child.props.tabIndex ?? 0,
            className: cx('trigger-element', child.props.className, { active: visible, disabled }),
            onClick: handleClickTrigger,
            onFocus: handleFocusBlurTrigger,
            onBlur: handleFocusBlurTrigger,
        };
        triggerElement = React__namespace.cloneElement(child, triggerProps);
    }
    const activeOptions = React__namespace.useMemo(() => (innerOptions ? innerOptions.filter((option) => !option.disabled) : []), [innerOptions]);
    const isActive = React__namespace.useCallback((value) => {
        if (innerValue === undefined) {
            return false;
        }
        if (isMultiple && Array.isArray(innerValue)) {
            return innerValue.includes(value);
        }
        return innerValue === value;
    }, [innerValue, isMultiple]);
    const setValue = React__namespace.useCallback((value) => {
        if (isMultiple && value !== undefined) {
            const pos = (innerValue || []).indexOf(value);
            const data = [...(innerValue || [])];
            pos >= 0 ? data.splice(pos, 1) : data.push(value);
            setInnerValue(data, (val) => {
                val && onChange?.(val);
            });
            return;
        }
        setInnerValue(value, (val) => {
            reactUtils.isDefined(val) && onChange?.(val);
        });
    }, [innerValue, isMultiple, onChange, setInnerValue]);
    const closeMenuOnSelect = React__namespace.useCallback((value) => (reactUtils.isDefined(closeOnSelect) && reactUtils.isFunction(closeOnSelect) ? closeOnSelect(value) : closeOnSelect), [closeOnSelect]);
    const handleSelect = React__namespace.useCallback((value, event) => {
        setValue(value);
        event && onClick?.(event);
        !isMultiple && closeMenuOnSelect(value) && setVisible.off();
    }, [setValue, onClick, isMultiple, closeMenuOnSelect, setVisible]);
    const getCurrentIndex = React__namespace.useCallback((value) => {
        if (!Array.isArray(innerOptions) || !value)
            return -1;
        if (isMultiple && Array.isArray(value)) {
            return activeOptions.findIndex((option) => option.value === value[value.length - 1]);
        }
        return activeOptions.findIndex((option) => option.value === value);
    }, [innerOptions, activeOptions, isMultiple]);
    const handleMouseMove = React__namespace.useCallback(() => {
        setFocusedIndex(getCurrentIndex(innerValue));
    }, [innerValue, getCurrentIndex]);
    const sortOptions = React__namespace.useCallback(() => {
        if (innerValue == null || !options)
            return;
        if (Array.isArray(innerValue)) {
            const selectedOptions = innerValue
                .map((value) => options.find((option) => option.value === value))
                .filter((option) => option !== undefined);
            setInnerOptions(Array.from(new Set([...selectedOptions, ...options])));
            return;
        }
        const sortedOptions = [...options].sort((item) => {
            if (item.value !== innerValue)
                return 1;
            return -1;
        });
        setInnerOptions(sortedOptions);
    }, [innerValue, options]);
    const getFocusedValue = React__namespace.useCallback(() => {
        const item = activeOptions[focusedIndex];
        return item ? item.value : null;
    }, [activeOptions, focusedIndex]);
    const menu = React__namespace.useMemo(() => (React__namespace.createElement(index.Menu, { className: cx('menu') }, innerOptions.map(({ label, value, disabled, ...args }) => (React__namespace.createElement(index.Menu.Item, { ...args, key: value, value: value, disabled: disabled, active: isActive(value) || getFocusedValue() === value, onClick: disabled ? reactUtils.noop : (event) => handleSelect(value, event), onMouseOver: handleMouseMove }, label))))), [cx, innerOptions, isActive, getFocusedValue, handleMouseMove, handleSelect]);
    const content = React__namespace.useMemo(() => {
        if (innerOptions.length)
            return menu;
        return reactUtils.isDefined(emptyContent) ? (emptyContent) : (React__namespace.createElement(index.Menu, null,
            React__namespace.createElement(index.Menu.Item, { disabled: true },
                React__namespace.createElement(Text.Text, { className: "h-text-center h-color-D60" }, defaultEmptyContentText))));
    }, [innerOptions, emptyContent, menu]);
    const defaultFooter = React__namespace.useMemo(() => (React__namespace.createElement(Button.Button, { type: "button", color: "blue", variant: "primary", onClick: setVisible.off }, "\u0413\u043E\u0442\u043E\u0432\u043E")), [setVisible.off]);
    reactUtils.useKeyPress({
        enabled: visible || focused.current,
        keyMap: {
            ArrowDown: (event) => {
                event.preventDefault();
                if (!visible) {
                    setVisible.on();
                    return;
                }
                const nextIndex = reactUtils.getNextIndex(focusedIndex, activeOptions.length);
                setFocusedIndex(nextIndex);
            },
            ArrowUp: (event) => {
                event.preventDefault();
                if (!visible) {
                    setVisible.on();
                    return;
                }
                const prevIndex = reactUtils.getPrevIndex(focusedIndex, activeOptions.length);
                setFocusedIndex(prevIndex);
            },
            Enter: (event) => {
                event.preventDefault();
                const value = getFocusedValue();
                if (visible && value !== undefined && value !== null) {
                    handleSelect(value);
                }
            },
            Escape: (event) => {
                event.preventDefault();
                setVisible.off();
            },
            Tab: () => {
                setVisible.off();
                focused.current = false;
            },
        },
    }, [innerValue, focusedIndex, activeOptions, visible, isMultiple, closeOnSelect]);
    React__namespace.useEffect(() => {
        if (visible) {
            onOpen?.();
        }
        else {
            !isFirstRender.current && onClose?.();
        }
    }, [visible, onClose, onOpen]);
    React__namespace.useEffect(() => {
        if (reactUtils.isDefined(value)) {
            setInnerValue(value);
        }
        else {
            setFocusedIndex(-1);
            !isFirstRender.current && setInnerValue(undefined);
        }
        isFirstRender.current = false;
    }, [value, setInnerValue]);
    React__namespace.useEffect(() => {
        if (visible) {
            setFocusedIndex(getCurrentIndex(innerValue));
        }
    }, [visible, innerValue, getCurrentIndex]);
    React__namespace.useEffect(() => {
        setInnerOptions(options || []);
    }, [options]);
    React__namespace.useEffect(() => {
        optionsSort && !visible && sortOptions();
    }, [visible, optionsSort, sortOptions]);
    return (React__namespace.createElement(Popover.Popover, { "data-qa": "Dropdown", ref: ref, className: popoverClassNames, visible: visible, header: mobile ? header : null, content: content, footer: mobile ? defaultFooter : null, mobile: mobile, positionerSettings: makePositionerSettings(placement, matchWidth, flip), onClose: setVisible.off, fullscreen: fullscreen, ...restProps }, triggerElement));
});
Dropdown.defaultProps = {
    placement: 'bottom-start',
    matchWidth: false,
    optionsSort: true,
    closeOnSelect: true,
    fullscreen: true,
};
Dropdown.displayName = 'Dropdown';

exports.Dropdown = Dropdown;
exports.defaultEmptyContentText = defaultEmptyContentText;
//# sourceMappingURL=Dropdown.js.map
